import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    Filler,
    Title,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

ChartJS.register(
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    Filler,
    Title,
    annotationPlugin,
);

/**
 * ParabolaChart — premium teaching-quality math chart.
 *
 * Graph rules applied (per spec):
 *  - Chart.js `line` type with tension 0.4, 160 sampled points, parsing:false
 *  - Explicit x=0 and y=0 axis lines via chartjs-plugin-annotation
 *  - Visible x/y tick labels with light grid (#0f172a14)
 *  - Container: frosted-white, rounded-28px, soft shadow, generous padding
 *  - Minimum height 360px (configurable via `height`)
 *  - Roots / vertex shown as separate scatter datasets with bigger dots
 *  - Curves & markers can be `hidden` per fragment — controlled by parent
 *
 * Props
 *   curves: [{ id, fn, color, label, hidden?, dashed?, width? }]
 *   markers: [{ x, y, color, label?, hidden? }]
 *   range:  [xMin, xMax]
 *   yRange: optional [yMin, yMax]
 *   height: container height in px (chart fills minus padding)
 *   stepX, stepY: tick step sizes (default 1)
 *   xLabel, yLabel: axis titles
 */
const PALETTE = {
    primary: "#2563eb",
    indigo: "#4f46e5",
    green: "#10b981",
    red: "#ef4444",
    amber: "#f59e0b",
    teal: "#14b8a6",
    rose: "#f43f5e",
    slate: "#64748b",
};

const sampleCurve = (fn, xMin, xMax, steps = 160) => {
    const out = [];
    const dx = (xMax - xMin) / steps;
    for (let i = 0; i <= steps; i++) {
        const x = xMin + i * dx;
        const y = fn(x);
        out.push({ x, y: Number.isFinite(y) ? y : null });
    }
    return out;
};

const ParabolaChart = ({
    curves = [],
    markers = [],
    range = [-5, 5],
    yRange,
    height = 380,
    stepX = 1,
    stepY,
    xLabel = "x",
    yLabel = "y",
    title,
}) => {
    const [xMin, xMax] = range;
    const [yMin, yMax] = yRange || [-Math.max(...range.map(Math.abs)), Math.max(...range.map(Math.abs))];
    const yTick = stepY || Math.max(1, Math.round((yMax - yMin) / 10));

    const data = useMemo(() => {
        const datasets = [];
        // Build ALL curves always — use Chart.js `hidden` to toggle
        // visibility instead of filtering datasets in/out. This keeps
        // dataset indices stable across renders so Chart.js animates
        // cleanly instead of "fluctuating" on every fragment change.
        curves.forEach((c) => {
            datasets.push({
                type: "line",
                label: c.label || c.id,
                data: sampleCurve(c.fn, xMin, xMax, 160),
                parsing: false,
                hidden: !!c.hidden,
                borderColor: c.color || PALETTE.primary,
                backgroundColor: c.fill
                    ? `${c.color || PALETTE.primary}22`
                    : "transparent",
                borderWidth: c.width || 3.5,
                borderDash: c.dashed ? [6, 6] : [],
                pointRadius: 0,
                pointHoverRadius: 0,
                tension: 0.4,
                fill: c.fill ? "origin" : false,
                spanGaps: false,
            });
        });
        // markers — scatter-style dots on top. Same stability trick:
        // always render every marker dataset, use `hidden` to toggle.
        markers.forEach((m) => {
            datasets.push({
                type: "line",
                label: m.label || "marker",
                data: [{ x: m.x, y: m.y }],
                parsing: false,
                hidden: !!m.hidden,
                showLine: false,
                borderColor: m.color || PALETTE.red,
                backgroundColor: m.color || PALETTE.red,
                pointRadius: 8,
                pointHoverRadius: 9,
                pointBackgroundColor: m.color || PALETTE.red,
                pointBorderColor: "#ffffff",
                pointBorderWidth: 3,
            });
        });
        return { datasets };
    }, [curves, markers, xMin, xMax]);

    const annotations = useMemo(() => {
        const ann = {};
        if (yMin < 0 && yMax > 0) {
            ann.xAxisLine = {
                type: "line",
                yMin: 0,
                yMax: 0,
                borderColor: "rgba(15,23,42,0.55)",
                borderWidth: 2,
            };
        }
        if (xMin < 0 && xMax > 0) {
            ann.yAxisLine = {
                type: "line",
                xMin: 0,
                xMax: 0,
                borderColor: "rgba(15,23,42,0.55)",
                borderWidth: 2,
            };
        }
        // marker labels — always include but use `display` to toggle so
        // annotation IDs stay stable across renders.
        markers
            .filter((m) => m.label)
            .forEach((m, i) => {
                ann[`mlabel${i}`] = {
                    type: "label",
                    display: !m.hidden,
                    xValue: m.x,
                    yValue: m.y,
                    content: m.label,
                    color: m.color || PALETTE.red,
                    backgroundColor: "rgba(255,255,255,0.92)",
                    borderColor: m.color || PALETTE.red,
                    borderWidth: 1.5,
                    borderRadius: 6,
                    padding: { x: 6, y: 3 },
                    font: { size: 12, weight: "600" },
                    yAdjust: m.y === 0 ? 18 : -18,
                };
            });
        return ann;
    }, [xMin, xMax, yMin, yMax, markers]);

    const options = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 700, easing: "easeOutCubic" },
            // Disable animation on subsequent dataset updates triggered
            // by React re-renders — keeps the chart stable when the
            // user holds the play button (fragment ticking every 1s).
            // Initial mount still animates via `animation` above.
            transitions: {
                active: { animation: { duration: 0 } },
                resize: { animation: { duration: 0 } },
                show: { animations: { x: { duration: 400 }, y: { duration: 400 } } },
                hide: { animations: { x: { duration: 200 }, y: { duration: 200 } } },
            },
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
                title: title
                    ? {
                          display: true,
                          text: title,
                          color: "#334155",
                          font: { size: 13, weight: "600" },
                      }
                    : { display: false },
                annotation: { annotations },
            },
            scales: {
                x: {
                    type: "linear",
                    min: xMin,
                    max: xMax,
                    title: {
                        display: true,
                        text: xLabel,
                        color: "#334155",
                        font: { size: 13, weight: "700" },
                    },
                    grid: { color: "rgba(15,23,42,0.06)", drawTicks: false },
                    border: { color: "rgba(15,23,42,0.35)" },
                    ticks: {
                        stepSize: stepX,
                        color: "#475569",
                        font: { size: 11, weight: "500" },
                    },
                },
                y: {
                    type: "linear",
                    min: yMin,
                    max: yMax,
                    title: {
                        display: true,
                        text: yLabel,
                        color: "#334155",
                        font: { size: 13, weight: "700" },
                    },
                    grid: { color: "rgba(15,23,42,0.06)", drawTicks: false },
                    border: { color: "rgba(15,23,42,0.35)" },
                    ticks: {
                        stepSize: yTick,
                        color: "#475569",
                        font: { size: 11, weight: "500" },
                    },
                },
            },
        }),
        [xMin, xMax, yMin, yMax, stepX, yTick, xLabel, yLabel, title, annotations],
    );

    return (
        <div
            className="w-full"
            style={{
                background: "rgba(255,255,255,0.82)",
                borderRadius: 28,
                boxShadow: "0 24px 60px rgba(15,23,42,0.10)",
                border: "1px solid rgba(255,255,255,0.7)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                padding: 18,
            }}
        >
            <div style={{ height, position: "relative" }}>
                <Line data={data} options={options} />
            </div>
        </div>
    );
};

export { PALETTE };
export default ParabolaChart;
